import { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth, useTrip } from '@trip-planner-monorepo/shared';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ExportImportModal,
  TripExportModal,
  TripImportModal,
  helpAssistance,
  DiscussionOverlay
} from '@trip-planner-monorepo/app-features';
import { UploadModal } from '@trip-planner-monorepo/documents';
import FileMenu from '../FileMenu';
import { pdfExportController } from '../../controllers/PDFExportController';
import { itineraryExportController } from '../../controllers/ItineraryExportController';
import { tripExportController } from '../../controllers/TripExportController';
import { tripImportController } from '../../controllers/TripImportController';
import './Header.css';
import SearchBar from './SearchBar';
import HealthCheckModal from '../HealthCheck/HealthCheckModal';
import {
  FiEdit3,
  FiTool,
  FiCpu,
  FiUploadCloud,
  FiUsers,
  FiMap,
  FiDollarSign,
  FiRefreshCw,
  FiCheckSquare,
  FiCloud,
  FiGlobe,
  FiPrinter,
  FiHeart,
  FiHelpCircle,
  FiUser,
  FiLogOut,
  FiSettings,
  FiChevronDown
} from 'react-icons/fi';

function Header() {
  const [showEditMenu, setShowEditMenu] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDiscussion, setShowDiscussion] = useState(false);
  const [showExportImportModal, setShowExportImportModal] = useState(false);
  const [exportImportTab, setExportImportTab] = useState('export');
  const [showTripExportModal, setShowTripExportModal] = useState(false);
  const [showTripImportModal, setShowTripImportModal] = useState(false);
  const [showHealthCheck, setShowHealthCheck] = useState(false);
  const { currentUser, logout } = useAuth();
  const { selectedTripId, currentTrip, effectiveUserId } = useTrip();
  const navigate = useNavigate();

  // Memoize export context so the object reference only changes when its values do.
  // Without this, an inline `context={{ ... }}` object would be recreated on every
  // Header render, causing TripExportModal's useEffect to re-fire unnecessarily.
  const tripExportContext = useMemo(() => ({
    tripId: selectedTripId,
    trip: currentTrip,
    user: currentUser,
    effectiveUserId
  }), [selectedTripId, currentTrip, currentUser, effectiveUserId]);
  const location = useLocation();

  const editMenuRef = useRef(null);
  const toolsMenuRef = useRef(null);
  const accountMenuRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editMenuRef.current && !editMenuRef.current.contains(event.target)) {
        setShowEditMenu(false);
      }
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target)) {
        setShowToolsMenu(false);
      }
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setShowAccountMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleEditMenu = () => {
    setShowEditMenu(!showEditMenu);
    setShowToolsMenu(false);
    setShowAccountMenu(false);
  };

  const toggleToolsMenu = () => {
    setShowToolsMenu(!showToolsMenu);
    setShowEditMenu(false);
    setShowAccountMenu(false);
  };

  const toggleAccountMenu = () => {
    setShowAccountMenu(!showAccountMenu);
    setShowEditMenu(false);
    setShowToolsMenu(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  // Legacy handlers for backwards compatibility (can be removed later)
  const _handleOpenExport = () => {
    setExportImportTab('export');
    setShowExportImportModal(true);
  };

  const _handleOpenImport = () => {
    setExportImportTab('import');
    setShowExportImportModal(true);
  };

  const handleCloseExportImport = () => {
    setShowExportImportModal(false);
  };

  const handleOpenSettings = () => {
    setExportImportTab('export');
    setShowExportImportModal(true);
  };

  // New File Menu handlers
  const handleNewTrip = () => {
    console.log('New Trip functionality will be implemented');
    // TODO: Navigate to trip creation or open new trip dialog
    navigate('/');
  };

  const handleOpenTrip = () => {
    console.log('Open Trip functionality will be implemented');
    // TODO: Show trip selection dialog or navigate to trips list
    navigate('/');
  };

  const handleSavePDF = async () => {
    const context = {
      pathname: location.pathname,
      trip: currentTrip,
      user: currentUser,
      tripId: selectedTripId
    };

    await pdfExportController.exportCurrentPage(context);
  };

  const handleExportItinerary = async () => {
    const context = {
      tripId: selectedTripId,
      trip: currentTrip,
      user: currentUser
    };

    // Use new modal-based controller method
    await itineraryExportController.showItineraryExportModal(context);
  };

  const handleExportTrip = () => {
    // Show the trip export modal
    setShowTripExportModal(true);
  };

  const handleImportTrip = () => {
    // Show the trip import modal
    setShowTripImportModal(true);
  };

  const handleImportSuccess = () => {
    // Navigate to Trips page after successful import
    // Force reload by navigating with replace and adding timestamp
    navigate('/', { replace: true, state: { refresh: Date.now() } });
    // Also force a full page reload to refresh the trips list
    window.location.href = '/';
  };

  const handlePrint = () => {
    console.log('Print functionality coming soon');
  };

  const handleSettings = () => {
    // For now, open the legacy export/import modal settings
    setExportImportTab('export');
    setShowExportImportModal(true);
  };

  const handleHealthCheck = () => {
    setShowHealthCheck(true);
  };

  const handleOpenDocumentUpload = () => {
    setShowToolsMenu(false);
    setShowUploadModal(true);
  };

  const handleOpenDiscussion = () => {
    setShowToolsMenu(false);
    setShowDiscussion(true);
  };

  const handleOpenTravelAgent = () => {
    setShowToolsMenu(false);
    navigate('/travel-agent');
  };

  const handleHelp = () => {
    // Determine context based on URL
    const path = location.pathname;
    let context = 'general';

    if (path.includes('/documents')) context = 'documents';
    else if (path.includes('/accommodation')) context = 'accommodation';
    else if (path.includes('/travel')) context = 'travel';
    else if (path.includes('/places')) context = 'places';
    else if (path.includes('/day')) context = 'days';
    else if (path.includes('/trips/') && path.split('/').length === 3) context = 'days'; // Main trip view
    else if (path === '/') context = 'general';

    helpAssistance.showHelp(context);
  };

  return (
    <header className="app-header">
      <nav className="main-menu" aria-label="Main navigation">
        <div
          className="app-branding"
          onClick={() => navigate('/')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
          role="button"
          tabIndex={0}
          aria-label="Go to home page"
        >
          <img src="/splash-icon.png" alt="Trip Planner Logo" className="app-logo" />
          <span className="app-title">itineri</span>
        </div>

        <div className="left-controls">
          <FileMenu
            onNewTrip={handleNewTrip}
            onOpenTrip={handleOpenTrip}
            onSavePDF={handleSavePDF}
            onExportItinerary={handleExportItinerary}
            onExportTrip={handleExportTrip}
            onImportTrip={handleImportTrip}
            onPrint={handlePrint}
            onSettings={handleSettings}
          />

          <div className={`menu-item ${showEditMenu ? 'is-open' : ''}`} ref={editMenuRef}>
            <button className="menu-btn" onClick={toggleEditMenu}>
              <FiEdit3 className="menu-icon" />
              <span>Edit</span>
              <FiChevronDown className="chevron-icon" />
            </button>
            {showEditMenu && (
              <div className="dropdown-menu">
                <button><span className="dropdown-label">Undo</span><span className="shortcut">Ctrl+Z</span></button>
                <button><span className="dropdown-label">Redo</span><span className="shortcut">Ctrl+Y</span></button>
                <div className="menu-separator"></div>
                <button><span className="dropdown-label">Cut</span><span className="shortcut">Ctrl+X</span></button>
                <button><span className="dropdown-label">Copy</span><span className="shortcut">Ctrl+O</span></button>
                <button><span className="dropdown-label">Paste</span><span className="shortcut">Ctrl+V</span></button>
              </div>
            )}
          </div>

          <div className={`menu-item ${showToolsMenu ? 'is-open' : ''}`} ref={toolsMenuRef}>
            <button className="menu-btn" onClick={toggleToolsMenu}>
              <FiTool className="menu-icon" />
              <span>Tools</span>
              <FiChevronDown className="chevron-icon" />
            </button>
            {showToolsMenu && (
              <div className="dropdown-menu tools-dropdown">
                <button onClick={handleOpenTravelAgent}>
                  <span className="dropdown-item-label"><FiCpu className="dropdown-icon" /> My Travel Agent</span>
                </button>
                <button onClick={handleOpenDocumentUpload}>
                  <span className="dropdown-item-label"><FiUploadCloud className="dropdown-icon" /> Document Upload</span>
                </button>
                <button onClick={handleOpenDiscussion}>
                  <span className="dropdown-item-label"><FiUsers className="dropdown-icon" /> Discussion Groups</span>
                </button>
                <button disabled>
                  <span className="dropdown-item-label"><FiMap className="dropdown-icon" /> Maps</span>
                  <span className="badge-soon">Soon</span>
                </button>
                <button disabled>
                  <span className="dropdown-item-label"><FiDollarSign className="dropdown-icon" /> Accounting</span>
                  <span className="badge-soon">Soon</span>
                </button>
                <button disabled>
                  <span className="dropdown-item-label"><FiRefreshCw className="dropdown-icon" /> Currency Converter</span>
                  <span className="badge-soon">Soon</span>
                </button>
                <button disabled>
                  <span className="dropdown-item-label"><FiCheckSquare className="dropdown-icon" /> Packing List</span>
                  <span className="badge-soon">Soon</span>
                </button>
                <button disabled>
                  <span className="dropdown-item-label"><FiCloud className="dropdown-icon" /> Weather</span>
                  <span className="badge-soon">Soon</span>
                </button>
                <button disabled>
                  <span className="dropdown-item-label"><FiGlobe className="dropdown-icon" /> Visa &amp; Entry</span>
                  <span className="badge-soon">Soon</span>
                </button>
              </div>
            )}
          </div>

          <button className="action-btn" onClick={handlePrint}>
            <FiPrinter className="menu-icon" />
            <span>Print</span>
          </button>

          <button className="action-btn" onClick={handleHealthCheck}>
            <FiHeart className="menu-icon heart-icon" />
            <span>Health Check</span>
          </button>

          <button className="action-btn help-btn" onClick={handleHelp}>
            <FiHelpCircle className="menu-icon help-icon" />
            <span>Help</span>
          </button>
        </div>

        <div className="account-center">
          <div className={`menu-item account-menu ${showAccountMenu ? 'is-open' : ''}`} ref={accountMenuRef}>
            <button className="account-pill" onClick={toggleAccountMenu}>
              <FiUser className="account-icon" />
              <span className="user-email">{currentUser?.email}</span>
              <FiChevronDown className="chevron-icon" />
            </button>
            {showAccountMenu && (
              <div className="dropdown-menu right-aligned">
                <button onClick={() => navigate('/profile')}>
                  <FiSettings className="dropdown-icon" /> Profile Settings
                </button>
                <button onClick={() => navigate('/change-password')}>
                  <FiEdit3 className="dropdown-icon" /> Change Password
                </button>
                <div className="menu-separator"></div>
                <button onClick={handleLogout} className="logout-btn">
                  <FiLogOut className="dropdown-icon" /> Logout
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="search-right">
          <SearchBar onOpenSettings={handleOpenSettings} />
        </div>
      </nav>

      {/* Export/Import Modal */}
      <ExportImportModal
        userId={currentUser?.uid}
        tripId={selectedTripId}
        isOpen={showExportImportModal}
        onClose={handleCloseExportImport}
        initialTab={exportImportTab}
      />

      {/* Trip Export Modal */}
      <TripExportModal
        open={showTripExportModal}
        onClose={() => setShowTripExportModal(false)}
        context={tripExportContext}
        controller={tripExportController}
      />

      {/* Trip Import Modal */}
      <TripImportModal
        open={showTripImportModal}
        onClose={() => setShowTripImportModal(false)}
        context={{
          user: currentUser,
          tripId: selectedTripId,
          trip: currentTrip
        }}
        controller={tripImportController}
        onImportSuccess={handleImportSuccess}
      />

      {/* Health Check Modal */}
      <HealthCheckModal
        isOpen={showHealthCheck}
        onClose={() => setShowHealthCheck(false)}
        trip={currentTrip}
      />

      {/* Document Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onProcessed={() => setShowUploadModal(false)}
        />
      )}

      {/* Discussion Groups Overlay */}
      <DiscussionOverlay
        isOpen={showDiscussion}
        onClose={() => setShowDiscussion(false)}
        tripId={selectedTripId}
      />
    </header>
  );
}

export default Header;